import { Component, Input, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InventoryService } from '@servicios/inventory/inventory.service';
import { NotificacionesService } from '@servicios/notificaciones.service';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule } from 'primeng/fileupload';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

@Component({
  selector: 'app-file-attachments',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    FileUploadModule,
    TableModule,
    TooltipModule,
    TagModule,
    ProgressSpinnerModule
  ],
  templateUrl: './file-attachments.component.html',
  styleUrls: ['./file-attachments.component.css']
})
export class FileAttachmentsComponent implements OnInit {
  private inventoryService = inject(InventoryService);
  private ns = inject(NotificacionesService);

  @Input() referenceId!: string;
  @Input() folder: string = 'equipments';
  @Input() title: string = 'Archivos Adjuntos';

  files = signal<any[]>([]);
  loading = signal(false);
  uploading = signal(false);

  ngOnInit() {
    if (this.referenceId) {
      this.loadFiles();
    }
  }

  async loadFiles() {
    this.loading.set(true);
    try {
      const data = await this.inventoryService.getFilesByReferenceId(this.referenceId);
      // files-ms returns an array of file metadata
      this.files.set(data);
    } catch (error) {
      console.error('Error loading files:', error);
      this.ns.emitMessage('Error al cargar archivos', true, 'error');
    } finally {
      this.loading.set(false);
    }
  }

  async onUpload(event: any) {
    const file = event.files[0];
    if (!file) return;

    this.uploading.set(true);
    try {
      await this.inventoryService.uploadFile(file, this.folder, this.referenceId);
      this.ns.emitMessage('Archivo subido con éxito');
      await this.loadFiles();
    } catch (error) {
      console.error('Error uploading file:', error);
      this.ns.emitMessage('Error al subir archivo', true, 'error');
    } finally {
      this.uploading.set(false);
    }
  }

  async deleteFile(file: any) {
    // files-ms delete expects folder and name
    // name in the backend is usually 'folder/filename'
    const parts = file.name.split('/');
    const folder = parts[0];
    const name = parts.slice(1).join('/');

    try {
      await this.inventoryService.deleteFile(folder, name);
      this.ns.emitMessage('Archivo eliminado');
      await this.loadFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      this.ns.emitMessage('Error al eliminar archivo', true, 'error');
    }
  }

  downloadFile(file: any) {
    // If files-ms returns a full URL, we can just open it
    if (file.url) {
      window.open(file.url, '_blank');
    } else {
      this.ns.emitMessage('URL de archivo no disponible', true, 'error');
    }
  }

  getFileIcon(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'pi pi-file-pdf text-red-500';
      case 'doc':
      case 'docx': return 'pi pi-file-word text-blue-500';
      case 'xls':
      case 'xlsx': return 'pi pi-file-excel text-green-500';
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif': return 'pi pi-image text-purple-500';
      default: return 'pi pi-file text-500';
    }
  }

  formatSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
